/**
 * Transfer Configuration for Chemicals (Raw Materials)
 * Uses current_unit_quantity for lot selection (whole units — drums/bags/totes).
 * Inserts raw_materials_transactions with unit_quantity + total_quantity.
 * DB triggers handle FIFO reduction and destination item updates.
 */

import { supabase } from '../../../../../supabaseClient';

export const transferChemicalConfig = {
  type:              'chemicals',
  displayName:       'Chemicals',
  singularName:      'Chemical',
  itemIdField:       'raw_material_id',
  itemNameField:     'name',

  inventoryTable:    'raw_materials_inventory',
  itemsTable:        'raw_materials_inventory_items',
  transactionsTable: 'raw_materials_transactions',

  /**
   * Get available lot items for a chemical at a location.
   * Returns a current_quantity alias (= current_unit_quantity) so the
   * generic TransferInventoryStepper table works without changes.
   */
  getInventoryItems: async (companyId, itemId, locationId) => {
    const { data, error } = await supabase
      .from('raw_materials_inventory_items')
      .select(`
        *,
        raw_materials (
          id, name, sku,
          primary_unit_type,
          primary_unit_quantity,
          primary_unit_measure
        )
      `)
      .eq('company_id', companyId)
      .eq('raw_material_id', itemId)
      .eq('location_id', locationId)
      .eq('is_active', true)
      .gt('current_unit_quantity', 0)
      .order('created_at', { ascending: true });

    if (error) throw error;

    // Normalize: alias current_quantity so the stepper renders correctly
    return (data || []).map(item => ({
      ...item,
      current_quantity: item.current_unit_quantity
    }));
  },

  /**
   * Execute transfer transactions for all selected lots.
   * total_quantity is computed from unit_quantity × primary_unit_quantity.
   */
  executeTransfer: async (companyId, transferData) => {
    const { fromLocationId, toLocationId, transfers, notes } = transferData;

    for (const transfer of transfers) {
      const { itemId, lotId, quantity, lot_number, batch_number, quantityMode } = transfer;

      // Fetch lot to get unit size for total_quantity computation
      const { data: lotData } = await supabase
        .from('raw_materials_inventory_items')
        .select('raw_material_id, raw_materials(primary_unit_quantity)')
        .eq('id', lotId)
        .single();

      const unitSize = parseFloat(lotData?.raw_materials?.primary_unit_quantity) || 1;
      let unitQty, totalQty;
      if (quantityMode === 'measure') {
        totalQty = parseFloat(parseFloat(quantity).toFixed(4));
        unitQty  = parseFloat((totalQty / unitSize).toFixed(4));
      } else {
        unitQty  = parseInt(quantity, 10);
        totalQty = parseFloat((unitQty * unitSize).toFixed(4));
      }

      const { error: txError } = await supabase
        .from('raw_materials_transactions')
        .insert([{
          company_id:       companyId,
          raw_material_id:  itemId,
          transaction_type: 'transfer',
          unit_quantity:    unitQty,
          total_quantity:   totalQty,
          from_location_id: fromLocationId,
          to_location_id:   toLocationId,
          location_id:      toLocationId,
          lot_number:       lot_number,
          batch_number:     batch_number,
          transaction_date: new Date().toISOString(),
          notes:            notes || null
        }]);

      if (txError) throw txError;

      // Activate any destination items created by DB trigger
      await supabase
        .from('raw_materials_inventory_items')
        .update({ is_active: true })
        .eq('company_id', companyId)
        .eq('raw_material_id', itemId)
        .eq('location_id', toLocationId)
        .gt('current_unit_quantity', 0);
    }
  }
};
